/**
 * The Odoo shell of an environment: the Deployment `<release>-shell` the odoo
 * chart (0.9.8 and later) ships at 0 replicas, whose pod carries the
 * `odoo-shell` command and stops on its own after 30 minutes without a
 * session. The branch page starts it, waits for its pod and opens the
 * embedded terminal on it; the pod's row stops it.
 *
 * Starting and stopping go through `waadoo-platform`
 * (`POST /projects/:project/namespaces/:namespace/shell/{start,stop}`,
 * backend `terminal/shellRoute.ts`), which has ArgoCD run its `shell-start` /
 * `shell-stop` resource actions AS THE USER, with the id_token sent here in
 * the body — never in the URL. The backend finds the Deployment in the
 * Application's tree itself: this module names the Application only.
 *
 * Plain functions, no hook and no translation: `useOdooShell.ts` turns them
 * into the page's state and words.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import type { OpenIdConnectApi } from '@backstage/core-plugin-api';
import { type PodInfo, type ResourceNode } from '@internal/plugin-waadoo-core';
import { isShellPod } from './odooPods';

/** How often the tree is read again while the shell pod starts. */
export const SHELL_POLL_INTERVAL_MS = 2_000;

/** How long the page waits for the shell pod before giving up. */
export const SHELL_START_TIMEOUT_MS = 120_000;

/** How long the page waits for the shell pod to go once stopped. */
export const SHELL_STOP_TIMEOUT_MS = 60_000;

/** The two actions, as the backend's URL names them. */
export type ShellAction = 'start' | 'stop';

/**
 * Why an action failed, as the page words it: `forbidden` (Backstage or
 * ArgoCD refused), `missing` (no shell Deployment: a chart older than
 * 0.9.8), `noIdentity` (no Authelia session), `failed` (anything else).
 */
export type ShellFailure = 'forbidden' | 'missing' | 'noIdentity' | 'failed';

export class ShellActionError extends Error {
  readonly failure: ShellFailure;

  constructor(failure: ShellFailure, message: string) {
    super(message);
    this.name = 'ShellActionError';
    this.failure = failure;
  }
}

/**
 * Whether the Application ships a shell: one `apps` Deployment of the
 * environment's namespace whose name ends with `-shell` — the rule the
 * backend applies before it runs an action. Without one (charts before
 * 0.9.8) the page offers nothing.
 */
export function hasShellDeployment(
  nodes: ResourceNode[],
  namespace: string | undefined,
): boolean {
  if (!namespace) {
    return false;
  }
  return (
    nodes.filter(
      node =>
        node.group === 'apps' &&
        node.kind === 'Deployment' &&
        node.namespace === namespace &&
        node.name.endsWith('-shell'),
    ).length === 1
  );
}

/**
 * A shell pod a terminal can open on: Healthy — what ArgoCD says of a
 * running pod whose containers are all ready (gitops-engine
 * `getCorev1PodHealth`) — and not being deleted (`Terminating`, ArgoCD's
 * « Status Reason » of a pod with a deletion timestamp).
 */
export function readyShellPod(pods: PodInfo[]): PodInfo | undefined {
  return pods.find(
    pod =>
      isShellPod(pod) &&
      pod.healthStatus === 'Healthy' &&
      pod.statusReason !== 'Terminating',
  );
}

export interface ShellActionRequest {
  fetchApi: FetchApi;
  discoveryApi: Pick<DiscoveryApi, 'getBaseUrl'>;
  autheliaAuth: Pick<OpenIdConnectApi, 'getIdToken'>;
  project: string;
  namespace: string;
  /** The branch's ArgoCD Application. */
  appName: string;
  action: ShellAction;
}

/** Asks the backend to start or stop the shell; rejects with a {@link ShellActionError}. */
export async function requestShellAction(
  options: ShellActionRequest,
): Promise<void> {
  const { fetchApi, discoveryApi, autheliaAuth, project, namespace } = options;
  // optional: the same session as every ArgoCD read of the page; without
  // one the page says so rather than open a sign-in popup.
  const idToken = await autheliaAuth.getIdToken({ optional: true });
  if (!idToken) {
    throw new ShellActionError('noIdentity', 'No Authelia session');
  }
  // Absolute backend URL: in development the frontend runs on another port.
  const baseUrl = await discoveryApi.getBaseUrl('waadoo-platform');
  const response = await fetchApi.fetch(
    `${baseUrl}/projects/${encodeURIComponent(
      project,
    )}/namespaces/${encodeURIComponent(namespace)}/shell/${options.action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appName: options.appName, idToken }),
    },
  );
  if (response.ok) {
    return;
  }
  const message = `Odoo shell ${options.action}: HTTP ${response.status}`;
  if (response.status === 403) {
    throw new ShellActionError('forbidden', message);
  }
  if (response.status === 404) {
    throw new ShellActionError('missing', message);
  }
  throw new ShellActionError('failed', message);
}
